import React from 'react'
import PropTypes from 'prop-types'
import styles from '../../css/Card.module.css'
import { Box, Button, Card, CardActionArea, CardActions, CardContent, Checkbox, Chip, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { DateTime } from 'luxon'
import UploadCardMedia from './UploadCardMedia'
import ShareButton from './ShareButton'
import ViewButton from './ViewButton'
import { uploadStatusLabel } from '../upload/UploadProcessingStatus'
import { gameName, isCombinedVideo, worstStatusPart } from '../../service/gameParts'
import { formatDuration, isCombinable } from '../../service/combine'
import { useAuth } from '../../auth/AuthContext'

UploadPreviewCard.propTypes = {
  upload: PropTypes.object,
  parts: PropTypes.arrayOf(PropTypes.object),
  partial: PropTypes.bool,
  busy: PropTypes.bool,
  onCombine: PropTypes.func,
  onRetryCombine: PropTypes.func,
  onUndoCombine: PropTypes.func,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onSelect: PropTypes.func
}

// One video, or (with parts) a game still stored as separate parts: it opens the first part and links to the game.
// `partial` means the list is filtered or cut off, so the parts here may not be all of the game's.
// The combine actions (D34 release B) are the owner's: combine a game's parts, retry or undo a failed combine,
// and, in "Combine videos" mode, select the card (`selectable`) to combine it with others.
export default function UploadPreviewCard ({
  upload,
  parts,
  partial = false,
  busy = false,
  onCombine,
  onRetryCombine,
  onUndoCombine,
  selectable = false,
  selected = false,
  onSelect
}) {
  const { user } = useAuth()
  const game = Boolean(parts && parts.length > 1 && upload.gameId)
  const status = game ? worstStatusPart(parts) : upload
  const title = game ? gameName(upload) : upload.name
  const combined = isCombinedVideo(upload)
  const isOwner = Boolean(user && upload.owner === user.username)
  // Only every part of the game: a filtered list may hold just some of them.
  const canCombineGame = game && !partial && !selectable && onCombine && parts.every(part => isCombinable(part, user?.username))
  const canFixCombine = combined && isOwner && upload.status === 'failed' && !selectable
  const seconds = game
    ? (parts.every(part => part.durationSeconds > 0) ? parts.reduce((sum, part) => sum + part.durationSeconds, 0) : 0)
    : upload.durationSeconds
  return (
    <Card
      className={styles.card}
      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/upload/${upload.id}`}
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}
      >
        <Box sx={{ position: 'relative' }}>
          <UploadCardMedia upload={upload} />
          {seconds > 0
            ? <Typography
                component="span"
                variant="caption"
                title="Duration"
                sx={{ position: 'absolute', right: 8, bottom: 8, px: 0.75, borderRadius: 1, bgcolor: 'rgba(0,0,0,.78)', color: '#fff', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatDuration(seconds)}
              </Typography>
            : null}
        </Box>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              lineHeight: 1.25,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {title}
          </Typography>

          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {game
              ? <Typography variant="body2" color="text.secondary">
                  {partial ? `Game in parts · ${parts.length} in these results` : `Game in ${parts.length} parts`}
                </Typography>
              : null}
            {combined
              ? <Typography variant="body2" color="text.secondary">
                  {`Combined from ${upload.combinedFrom.length} parts`}
                </Typography>
              : null}
            <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                {upload.date ? DateTime.fromISO(upload.date).toLocaleString(DateTime.DATETIME_MED) : 'Date not set'}
              </Typography>
              <Chip
                size="small"
                label={uploadStatusLabel(status)}
                color={status.readyForPlayback ? 'success' : status.status === 'failed' ? 'error' : 'default'}
                variant={status.readyForPlayback ? 'filled' : 'outlined'}
              />
            </Stack>
            {upload.description
              ? <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {upload.description}
                </Typography>
              : null}
          </Stack>
        </CardContent>
      </CardActionArea>

      <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0, mt: 'auto', flexWrap: 'wrap', rowGap: 0.5 }}>
        {selectable
          ? <Checkbox
              size="small"
              checked={selected}
              onChange={event => onSelect && onSelect(event.target.checked)}
              inputProps={{ 'aria-label': `Select ${title}` }}
              sx={{ p: 0.5 }}
            />
          : null}
        <ViewButton url={`/upload/${upload.id}`} />
        {game
          ? <Button component={RouterLink} to={`/game/${upload.gameId}`} size="small" color="primary">Game details</Button>
          : <ShareButton url={`/upload/${upload.id}`} />}
        {canCombineGame
          ? <Button size="small" color="primary" disabled={busy} onClick={() => onCombine(parts)}>Combine into one video</Button>
          : null}
        {canFixCombine && onRetryCombine
          ? <Button size="small" color="primary" disabled={busy} onClick={() => onRetryCombine(upload)}>Try again</Button>
          : null}
        {canFixCombine && onUndoCombine
          ? <Button size="small" color="error" disabled={busy} onClick={() => onUndoCombine(upload)}>Undo</Button>
          : null}
      </CardActions>
    </Card>
  )
}
